import React, { useRef, useId, useState, useEffect } from 'react';
import { ExtendedWindowType } from '../coveo/engine';
import { INVALID_GOOGLE_ZIP_EVENT } from '../helpers/geolocation';

type ZipFormProps = {
  defaultZipValue?: string;
  defaultCountryValue: string;
  invalidZipMessage: string;
  onSubmit: (zip: string, country: string) => void;
  zipRegex: {
    [key: string]: string;
  };
  zipPlaceholders: { [key: string]: string };
  onError?: (error: string) => void;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  whiteBox?: boolean;
  onClear?: () => void;
  withSubmitButton?: boolean;
  submitLabel?: string;
  alternativeOptions?: boolean;
  disabled?: boolean;
  hideCountry?: boolean;
};

const ZIP_CODE_PLACEHOLDER = '{{zipCode}}';
const COUNTRY_VALUES = ['ca', 'us', 'can', 'usa'];

function ZipForm({
  defaultZipValue,
  invalidZipMessage,
  onSubmit,
  defaultCountryValue,
  zipRegex,
  zipPlaceholders,
  onError,
  onInput,
  whiteBox,
  onClear,
  withSubmitButton,
  submitLabel,
  alternativeOptions,
  disabled,
  hideCountry
}: ZipFormProps) {
  const id = useId();
  const zipRef = useRef<HTMLInputElement>(null);
  const [country, setCountry] = useState(
    COUNTRY_VALUES.includes(defaultCountryValue) ? defaultCountryValue : 'us'
  );
  const [modified, setModified] = useState(defaultZipValue ? true : false);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [zipValue, setZipValue] = useState(defaultZipValue);
  const [ready, setReady] = useState(defaultZipValue ? true : false);

  const handleInvalidZip = () => {
    const error = invalidZipMessage.replace(
      ZIP_CODE_PLACEHOLDER,
      zipRef.current?.value || ''
    );
    setInvalidMessage(error);
    if (onError) onError(error);
  };

  const checkZipValidity = () => {
    if (zipRef.current?.checkValidity()) {
      setReady(true);
      setInvalidMessage('');
    } else {
      const error = invalidZipMessage.replace(
        ZIP_CODE_PLACEHOLDER,
        zipRef.current?.value || ''
      );
      setInvalidMessage(error);
      if (onError) onError(error);
    }
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (ready && modified) {
      setModified(false);
      zipRef.current?.parentElement?.classList.remove('field--error');
      if (zipValue) {
        let _zipValue = zipValue;
        if (country.toLowerCase().includes('ca')) {
          const cleaned = _zipValue.replace(/\s/g, '');
          _zipValue = `${cleaned.substring(0, 3)} ${cleaned.substring(3)}`;
        }
        onSubmit(_zipValue, country);

        const global = window as ExtendedWindowType;
        global.dataLayer?.push({
          event: 'zip_code',
          event_action: 'zip code updated',
          event_category: 'geo info',
          event_attributes: {
            zip_code: _zipValue,
            zip_code_country: country.toLowerCase()
          }
        });
      }
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCountry(event.target.value);
    setZipValue('');
    setReady(false);
    setInvalidMessage('');
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setModified(true);
    setZipValue(newValue);
    setInvalidMessage('');
    setReady(false);

    if (onInput) onInput(event);

    if (newValue.length >= 5) {
      checkZipValidity();
    }
  };

  const handleClear = () => {
    setInvalidMessage('');
    setZipValue('');
    setReady(false);
    if (onClear) onClear();
  };

  useEffect(() => {
    window.addEventListener(INVALID_GOOGLE_ZIP_EVENT, handleInvalidZip);

    return () => {
      window.removeEventListener(INVALID_GOOGLE_ZIP_EVENT, handleInvalidZip);
    };
  }, []);

  return (
    <>
      <form
        action=""
        onSubmit={handleFormSubmit}
        className={`zip-form ${whiteBox ? 'zip-form--white-box' : ''} ${
          withSubmitButton ? 'zip-form--with-cta' : ''
        }`}
        noValidate
      >
        {!hideCountry && (
          <div
            className={`field field--select zip-form__country zip-form__country--${country}`}
          >
            <label
              htmlFor={`select-country${id}`}
              className="field__label sr-only"
            >
              Country
            </label>
            <select
              value={country}
              name={`select-country${id}`}
              id={`select-country${id}`}
              className="field__select zip-form__country-select"
              onChange={handleCountryChange}
              disabled={disabled}
            >
              {alternativeOptions ? (
                <>
                  <option value="can">CA</option>
                  <option value="usa">US</option>
                </>
              ) : (
                <>
                  <option value="ca">CA</option>
                  <option value="us">US</option>
                </>
              )}
            </select>
          </div>
        )}

        <div
          data-module="input"
          className="field field--required zip-form__zip"
        >
          <label
            className="field__label sr-only"
            htmlFor={`select-country${id}`}
          >
            Zip
          </label>
          <input
            value={zipValue}
            ref={zipRef}
            className="field__input zip-form__zip-input"
            id={`select-country${id}`}
            name={`select-zip${id}`}
            placeholder={zipPlaceholders[country]}
            required={true}
            pattern={zipRegex[country]}
            onInput={handleInput}
            disabled={disabled}
          />
          {zipValue && (
            <button
              className="field__input-close"
              type="button"
              title="Clear"
              onClick={handleClear}
            >
              <span className="sr-only">Clear</span>
            </button>
          )}
          {!withSubmitButton && (
            <button
              className="field__input-search"
              type="submit"
              title="Submit"
              disabled={!ready}
            >
              <span className="sr-only">
                {submitLabel ? submitLabel : 'Submit'}
              </span>
            </button>
          )}
        </div>
        {withSubmitButton && (
          <div className="zip-form__submit-wrapper">
            <button
              type="submit"
              className="btn btn--primary zip-form__submit-btn"
              disabled={!ready}
              data-layer='[{"event":"find_a_contractor_button","event_category":"find a contractor","event_action":"find a contractor button","event_label":"","event_attributes":{"zip_code":null}}]'
            >
              {submitLabel ? submitLabel : 'Submit'}
            </button>
          </div>
        )}
      </form>
      {invalidMessage && (
        <div className="zip-form__error-message">
          <p className="error-message__error ">{invalidMessage}</p>
        </div>
      )}
    </>
  );
}

export default ZipForm;
